import * as React from 'react';
import {useSchemaNavigator} from './useSchemaNavigator';
import {EntityTypesList} from './EntityTypesList';
import {RelationsAndAttributesList} from './RelationsAndAttributesList';
import {Loading} from '../Loading';
import {AttributeValuesList} from './AttributeValuesList';

import '../css/schemanavigator.css';
import {SearchBar} from './SearchBar';
import {providers, client} from '@logilab/cwclientlibjs';
import {TranslationContext} from '../commons';

export const SchemaNavigator: React.FC<{
    schemaProvider: providers.RqlEntitySchemasLoader;
    handleError: (error: string) => void;
    showMetamodel: boolean;
    clientRql: client.CwRqlClient;

    entityTypeTextSearch: string;
    setEntityTypeTextSearch: (text: string) => void;
    relationsAndAttributesTextSearch: string;
    setRelationsAndAttributesTextSearch: (text: string) => void;
    linkedEntityTypeTextSearch: string;
    setLinkedEntityTypeTextSearch: (text: string) => void;
}> = ({
    schemaProvider,
    handleError,
    showMetamodel,
    clientRql,
    entityTypeTextSearch,
    setEntityTypeTextSearch,
    relationsAndAttributesTextSearch,
    setRelationsAndAttributesTextSearch,
    linkedEntityTypeTextSearch,
    setLinkedEntityTypeTextSearch,
}) => {
    const {
        schemaLoaded,
        filteredEntityTypes,
        relations,
        attributes,
        filteredLinkedEntityTypes,
        selectedAttributeValues,
        selectedEntityTypeName,
        selectedRelationName,
        selectedAttributeName,
        selectedLinkedEntityTypeName,
        tempSelectedEntityTypeName,
        setTempEntityTypeName,
        deselectTempEntityType,
        tempSelectedRelationOrAttributeName,
        setTempSelectedRelationOrAttribute,
        deselectTempRelationOrAttribute,
    } = useSchemaNavigator({
        schemaProvider,
        handleError,
        showMetamodel,
        clientRql,
        entityTypeTextSearch,
        setEntityTypeTextSearch,
        relationsAndAttributesTextSearch,
        setRelationsAndAttributesTextSearch,
        linkedEntityTypeTextSearch,
        setLinkedEntityTypeTextSearch,
    });

    if (!schemaLoaded) {
        return (
            <div className="schema-navigator">
                <Loading />
            </div>
        );
    }

    return (
        <div className="schema-navigator">
            <div className="column">
                <TranslationContext.Consumer>
                    {(tc) => (
                        <SearchBar
                            placeHolder={tc.translate('Subject Class')}
                            input={entityTypeTextSearch}
                            onChangeInput={setEntityTypeTextSearch}
                        />
                    )}
                </TranslationContext.Consumer>
                <EntityTypesList
                    entities={filteredEntityTypes}
                    selectedEntityTypeName={selectedEntityTypeName}
                    selectEntityType={(name) => {
                        setRelationsAndAttributesTextSearch('');
                        setLinkedEntityTypeTextSearch('');
                        if (name === selectedEntityTypeName) {
                            setEntityTypeTextSearch('');
                        } else {
                            setEntityTypeTextSearch(name);
                        }
                    }}
                    tempSelectedEntityTypeName={tempSelectedEntityTypeName}
                    setTempEntityTypeName={setTempEntityTypeName}
                    deselectTempEntityType={deselectTempEntityType}
                    textSearch={entityTypeTextSearch}
                />
            </div>
            <div className="column">
                <TranslationContext.Consumer>
                    {(tc) => (
                        <SearchBar
                            placeHolder={tc.translate('Relation')}
                            input={relationsAndAttributesTextSearch}
                            onChangeInput={setRelationsAndAttributesTextSearch}
                        />
                    )}
                </TranslationContext.Consumer>
                <RelationsAndAttributesList
                    relations={relations}
                    attributes={attributes}
                    selectedRelationName={selectedRelationName}
                    selectRelationOrAttribute={(name) => {
                        setLinkedEntityTypeTextSearch('');
                        if (
                            name === selectedRelationName ||
                            name === selectedAttributeName
                        ) {
                            setRelationsAndAttributesTextSearch('');
                        } else {
                            setRelationsAndAttributesTextSearch(name);
                        }
                    }}
                    selectedAttributeName={selectedAttributeName}
                    tempSelectedRelationOrAttribute={
                        tempSelectedRelationOrAttributeName
                    }
                    setTempSelectedRelationOrAttribute={
                        setTempSelectedRelationOrAttribute
                    }
                    deselectTempRelationOrAttribute={
                        deselectTempRelationOrAttribute
                    }
                    textSearch={relationsAndAttributesTextSearch}
                />
            </div>
            <div className="column">
                <TranslationContext.Consumer>
                    {(tc) => (
                        <SearchBar
                            placeHolder={tc.translate('Object Class')}
                            input={linkedEntityTypeTextSearch}
                            onChangeInput={setLinkedEntityTypeTextSearch}
                        />
                    )}
                </TranslationContext.Consumer>
                <EntityTypesList
                    entities={filteredLinkedEntityTypes}
                    selectedEntityTypeName={selectedLinkedEntityTypeName}
                    selectEntityType={(name) => {
                        if (name === selectedLinkedEntityTypeName) {
                            setLinkedEntityTypeTextSearch('');
                        } else {
                            setLinkedEntityTypeTextSearch(name);
                        }
                    }}
                    textSearch={linkedEntityTypeTextSearch}
                >
                    <AttributeValuesList
                        attributeValues={selectedAttributeValues}
                    />
                </EntityTypesList>
            </div>
        </div>
    );
};
